import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CtaLink } from "@/components/landing/CtaLink";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const }
  }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const FAQ_ITEMS = [
  {
    question: "Do we lose control if AI is generating our proposals?",
    answer: "Not at all. Sorvaine surfaces intelligence and drafts — your team reviews, edits, and approves every word. Nothing goes out without your sign-off."
  },
  {
    question: "How long does it take to get up and running?",
    answer: "Most teams are live within a day. Upload your content, and Sorvaine organises and indexes it automatically. Your first AI-assisted response can be ready in hours."
  },
  {
    question: "Will this work with our current tender and bid documents?",
    answer: "Yes. Sorvaine handles PDF, Word, and Excel — including complex tables, multi-section documents, and varied formatting. Exports to Word with your branding."
  },
  {
    question: "How does the AI use our existing content and past responses?",
    answer: "It learns your voice, capabilities, and win themes from your uploaded library. The more you use it, the sharper it gets — without ever sharing your data with other customers."
  },
  {
    question: "Is our proposal and company data secure?",
    answer: "Encrypted at rest and in transit. Your data is never used to train models for anyone else. We follow enterprise-grade security practices as standard."
  },
  {
    question: "Can our whole team collaborate inside the platform?",
    answer: "Yes. Assign sections, leave comments, track progress, and manage deadlines — all in one workspace with role-based permissions and version control."
  },
  {
    question: "What kind of support do we get after onboarding?",
    answer: "All plans include email support within 24 hours. Team and Enterprise plans get priority support, a dedicated success manager, and hands-on training."
  },
  {
    question: "Do we need to replace our current tools?",
    answer: "No. Sorvaine works alongside your existing stack. Import from your current documents, export in standard formats, and integrate with your workflow — not against it."
  }
];

export function FAQSection() {
  return (
    <section className="py-[80px] md:py-[100px] lg:py-[120px] relative z-10">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="text-sm font-normal tracking-[0.15em] uppercase text-muted-foreground">Q&A</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white/95 tracking-tight leading-[1.1]">
            Questions teams ask before signing up
          </h2>
          
          <p className="mt-8 text-base md:text-lg font-normal text-white/50 max-w-2xl mx-auto leading-[1.75]">
            Real questions from teams evaluating Sorvaine.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid lg:grid-cols-12 gap-12 lg:gap-16"
        >
          {/* FAQ Accordion */}
          <motion.div variants={fadeIn} className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-5">
              {FAQ_ITEMS.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-[#1A1A1A] rounded-lg px-5 md:px-8 landing-card data-[state=open]:bg-[#0A0A0C] transition-colors"
                >
                  <AccordionTrigger className="text-left py-6 hover:no-underline group">
                    <span className="text-base md:text-lg font-medium text-white/90 group-hover:text-white transition-colors pr-4">
                      {item.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm font-normal text-white/50 leading-[1.75] pb-8">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          {/* CTA Block */}
          <motion.div variants={fadeIn} className="lg:col-span-4">
            <div className="lg:sticky lg:top-8 p-7 md:p-10 rounded-lg border border-[#1A1A1A] landing-card text-center">
              <div className="w-14 h-14 rounded-full border border-primary/30 bg-primary/5 flex items-center justify-center mb-8 mx-auto">
                <MessageCircle className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-base md:text-lg font-medium tracking-tight text-white mb-4">
                Ready to get started?
              </h3>
              <p className="text-sm font-normal text-white/50 leading-[1.75] mb-3">
                Sign up and have your first AI-assisted response ready in hours.
              </p>
              <p className="text-xs font-normal text-white/35 leading-[1.7] mb-8">
                No credit card required to explore.
              </p>
              <CtaLink to="/signup" cta="get_started" location="faq">
                <Button className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground mb-4">
                  Get started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CtaLink>
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 text-white/40 hover:text-white/60 transition-colors"
              >
                <span className="text-xs font-normal">or contact us</span>
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
